/**
 * Lifecycle Action - WHAT Section
 *
 * Edit mode UI for assigning a lifecycle stage.
 * Used inline within AutomationCard when editing the "What" section.
 *
 * V2 Reference: v2/app/templates/components/client-reminder-lifecycle.hbs
 *
 * Fields:
 * - reminderOnId: Selected lifecycle stage ID
 */

import { useQuery } from '@tanstack/react-query';
import { useFormikContext } from 'formik';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Stack } from 'src/components/Stack';
import { fetchAllLifecycleStages } from 'src/models/lifecycle/lifecycle';
import { FormSelect } from 'src/shared/Forms/Fields/FormSelect';

import { ActionWhatSectionProps } from '../automationActions';

/**
 * Lifecycle WHAT Section - Edit mode
 */
export const LifecycleWhatSection: FC<ActionWhatSectionProps> = ({
  index,
}) => {
  const { t } = useTranslation();
  const { setFieldValue } = useFormikContext();

  // Fetch available lifecycle stages
  const { data: stages = [], isLoading } = useQuery({
    queryKey: ['lifecycle-stages'],
    queryFn: fetchAllLifecycleStages,
    staleTime: 60000,
  });

  const stageOptions = stages.map((stage) => ({
    value: stage.id.toString(),
    label: stage.name,
  }));

  return (
    <Stack spacing={2}>
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <Box>{t('automations.lifecycle.label')}</Box>
        <Box sx={{ minWidth: 200 }}>
          <FormSelect
            name={`automations.${index}.reminderOnId`}
            options={stageOptions}
            prompt={isLoading ? t('automations.common.loading') : t('automations.lifecycle.selectStage')}
            onChange={(e) => {
              setFieldValue(
                `automations.${index}.reminderOnId`,
                e.target.value
              );
              // V2 sets reminderOnType to 'LifecycleStage'
              setFieldValue(
                `automations.${index}.reminderOnType`,
                'LifecycleStage'
              );
            }}
          />
        </Box>
      </Box>
    </Stack>
  );
};

/**
 * Lifecycle What Summary Component
 */
export const LifecycleWhatSummary: FC<{
  automation: ActionWhatSectionProps['automation'];
}> = ({ automation }) => {
  const { t } = useTranslation();
  const { data: stages = [] } = useQuery({
    queryKey: ['lifecycle-stages'],
    queryFn: fetchAllLifecycleStages,
    staleTime: 60000,
  });

  if (!automation.reminderOnId) {
    return <i>{t('automations.lifecycle.noLifecycleSelected')}</i>;
  }

  const stage = stages.find(
    (s) => s.id.toString() === automation.reminderOnId?.toString()
  );
  const stageName = stage?.name || `Stage #${automation.reminderOnId}`;

  return (
    <>
      {t('automations.lifecycle.setLifecycleStageTo')} <b>{stageName}</b>
    </>
  );
};
